import Cookies from 'js-cookie';
import { revokeSession } from 'services/stytchAuthService';
import { store } from 'redux_store/store';
import { sessionInitAPI } from 'redux_store/services/sessionInit';
import { invalidateDropdownBundle } from 'hooks/useDropdownBundle';
import { clearCachedUserMetadata, getCanonicalUserId } from 'auth/auth';

/**
 * Known non-HttpOnly cookies set by form components that must be
 * explicitly cleared on logout. HttpOnly cookies (mira_session,
 * mira_refresh, access_token) are handled server-side by the
 * revokeSession() endpoint.
 */
const FORM_COOKIES = ['numberOfDependents', 'isMlsListed', 'idToken'];

/**
 * Perform a complete logout:
 * 1. Clear sessionStorage and localStorage first (prevents stale context if later steps fail)
 * 2. Clear Redux caches
 * 3. Remove non-HttpOnly cookies
 * 4. Call backend to revoke session (best-effort)
 * 5. Redirect to origin (login page)
 */
export async function performLogout() {
  // Secondary (tab-scoped) sessions only need sessionStorage cleanup.
  // Do NOT touch localStorage or cookies — those belong to the primary session.
  const isSecondary = sessionStorage.getItem('fm_secondary_session') === 'true';
  if (isSecondary) {
    try {
      await revokeSession();
    } catch { /* best-effort */ }
    sessionStorage.clear();
    window.location.href = window.location.origin;
    return;
  }

  // Primary session logout: signal other tabs first, then clean up. The event
  // is tagged 'primary_logout' (+ userId) so ONLY cookie-dependent tabs react;
  // independent secondary (tab-scoped Bearer) sessions in other tabs must
  // survive — logging out user A must not kill user B's tab.
  try {
    localStorage.setItem('fm_cross_tab_event', JSON.stringify({
      type: 'primary_logout',
      userId: getCanonicalUserId() || null,
      timestamp: Date.now(),
    }));
  } catch { /* ignore */ }

  // Step 1: Clear THIS tab's sessionStorage (tab-scoped, safe) + the primary
  // session's own localStorage keys. NEVER localStorage.clear() — localStorage
  // is shared across tabs, and a different user's secondary session in another
  // tab relies on it.
  sessionStorage.clear();
  try {
    const tokenManager = (await import('services/auth/tokenManager')).default;
    tokenManager.clearTokens();
  } catch { /* tokenManager may be unavailable */ }
  clearCachedUserMetadata();

  // Step 2: Clear session init cache and legacy dropdown bundle cache
  try {
    store.dispatch(sessionInitAPI.util.resetApiState());
    invalidateDropdownBundle();
  } catch {
    // Redux store may not be available
  }

  // Step 3: Remove known non-HttpOnly form cookies
  for (const name of FORM_COOKIES) {
    Cookies.remove(name);
    Cookies.remove(name, { path: '/' });
  }

  // Step 4: Expire any remaining visible cookies (belt-and-suspenders)
  const cookies = document.cookie.split('; ');
  for (const cookie of cookies) {
    const [name] = cookie.split('=');
    if (name) {
      document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  }

  // Step 5: Backend revocation (best-effort — clears HttpOnly cookies via Set-Cookie headers)
  try {
    await revokeSession();
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[Logout] Failed to revoke backend session', error);
    }
  }

  // Step 6: Redirect to login
  window.location.href = window.location.origin;
}
